import { Head, Link, router, useForm } from "@inertiajs/react"
import Cropper from "cropperjs"
import { useCallback, useEffect, useRef, useState } from "react"
import "cropperjs/dist/cropper.css"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface PhotoData {
  id: number
  title: string
  document_date: string | null
  document_time: string | null
  timezone_id: number | null
  user_id: number | null
  case_report_id: number | null
  notes: string
  significant: number
  width: number | null
  height: number | null
  image_url: string | null
  crop_rotate_url: string
}

interface Employee {
  id: number
  name: string
}

interface Report {
  id: number
  label: string
}

interface TimeZoneOption {
  id: number
  name: string
}

interface Props {
  case_record: CaseRecord
  photo: PhotoData
  employees: Employee[]
  reports: Report[]
  time_zones: TimeZoneOption[]
  errors?: Record<string, string[]>
}

export default function Edit({
  case_record,
  photo,
  employees,
  reports,
  time_zones,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Photos", href: `/cases/${case_record.id}/photos` },
    {
      title: `Edit Photo #${photo.id}`,
      href: `/cases/${case_record.id}/photos/${photo.id}/edit`,
    },
  ]

  const basePath = `/cases/${case_record.id}/photos`

  const { data, setData, processing } = useForm({
    title: photo.title,
    document_date: buildDateTime(photo.document_date, photo.document_time),
    timezone_id: photo.timezone_id ?? "",
    user_id: photo.user_id ?? "",
    case_report_id: photo.case_report_id ?? "",
    notes: photo.notes,
    significant: photo.significant,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    router.put(`${basePath}/${photo.id}`, { photo: data })
  }

  // Crop/rotate state
  const imageRef = useRef<HTMLImageElement>(null)
  const cropperRef = useRef<Cropper | null>(null)
  const [cropMode, setCropMode] = useState(false)
  const [cropProcessing, setCropProcessing] = useState(false)

  const initCropper = useCallback(() => {
    if (!imageRef.current) return
    if (cropperRef.current) {
      cropperRef.current.destroy()
    }
    cropperRef.current = new Cropper(imageRef.current, {
      viewMode: 2,
      autoCropArea: 1,
      background: false,
      rotatable: true,
      zoomable: false,
    })
  }, [])

  const destroyCropper = useCallback(() => {
    if (cropperRef.current) {
      cropperRef.current.destroy()
      cropperRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => destroyCropper()
  }, [destroyCropper])

  const handleEnterCropMode = () => {
    setCropMode(true)
    // Small delay to let the image render unconstrained
    setTimeout(initCropper, 100)
  }

  const handleCancelCrop = () => {
    destroyCropper()
    setCropMode(false)
  }

  const handleRotateLeft = () => {
    cropperRef.current?.rotate(-90)
  }

  const handleRotateRight = () => {
    cropperRef.current?.rotate(90)
  }

  const handleSaveCrop = () => {
    if (!cropperRef.current) return
    setCropProcessing(true)

    const cropData = cropperRef.current.getData(true)

    router.patch(
      photo.crop_rotate_url,
      {
        crop_x: Math.max(0, cropData.x),
        crop_y: Math.max(0, cropData.y),
        crop_width: cropData.width,
        crop_height: cropData.height,
        rotation: cropData.rotate,
      },
      {
        onFinish: () => {
          setCropProcessing(false)
          setCropMode(false)
          destroyCropper()
        },
      },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit Photo #${photo.id} - ${case_record.case_number}`} />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Edit Photo #{photo.id}</h1>

        {photo.image_url && (
          <div className="mb-6">
            <div className="text-center">
              <div
                className={cropMode ? "mx-auto max-w-3xl" : ""}
                style={cropMode ? { maxHeight: 500 } : undefined}
              >
                <img
                  ref={imageRef}
                  src={photo.image_url}
                  alt={photo.title || `Photo #${photo.id}`}
                  className={
                    cropMode
                      ? "block max-w-full"
                      : "mx-auto max-h-[300px] rounded"
                  }
                  key={cropMode ? "crop" : "view"}
                />
              </div>
              {photo.width && photo.height && !cropMode && (
                <div className="text-hpi-med-gray mt-1 text-sm">
                  {photo.width}x{photo.height}
                </div>
              )}
            </div>

            {/* Crop/Rotate controls */}
            <div className="mt-3 flex items-center justify-center gap-3">
              {!cropMode ? (
                <button
                  type="button"
                  onClick={handleEnterCropMode}
                  className="bg-hpi-blue hover:bg-hpi-dark-blue rounded px-4 py-2 text-sm text-white"
                >
                  Crop / Rotate
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleRotateLeft}
                    className="rounded border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50"
                    title="Rotate Left"
                  >
                    ↺ Rotate Left
                  </button>
                  <button
                    type="button"
                    onClick={handleRotateRight}
                    className="rounded border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50"
                    title="Rotate Right"
                  >
                    ↻ Rotate Right
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveCrop}
                    disabled={cropProcessing}
                    className="bg-hpi-green hover:bg-hpi-green/90 rounded px-4 py-2 text-sm text-white disabled:opacity-50"
                  >
                    {cropProcessing ? "Saving..." : "Apply"}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelCrop}
                    className="text-hpi-active-blue text-sm hover:underline"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mx-auto max-w-2xl space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">
              Title (optional)
            </label>
            <input
              type="text"
              value={data.title}
              onChange={(e) => setData("title", e.target.value)}
              maxLength={50}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            />
            {errors?.title && (
              <p className="text-hpi-red mt-1 text-sm">{errors.title[0]}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium">Date</label>
              <input
                type="date"
                value={
                  data.document_date ? data.document_date.split("T")[0] : ""
                }
                onChange={(e) => {
                  const time = data.document_date
                    ? data.document_date.split("T")[1] || "00:00:00"
                    : "00:00:00"
                  setData(
                    "document_date",
                    e.target.value ? `${e.target.value}T${time}` : "",
                  )
                }}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Time</label>
              <input
                type="time"
                step="1"
                value={
                  data.document_date
                    ? data.document_date.split("T")[1] || ""
                    : ""
                }
                onChange={(e) => {
                  const date = data.document_date
                    ? data.document_date.split("T")[0]
                    : new Date().toISOString().split("T")[0]
                  setData("document_date", `${date}T${e.target.value}`)
                }}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
            </div>
          </div>
          {errors?.document_date && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.document_date[0]}
            </p>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium">Time Zone</label>
            <select
              value={data.timezone_id}
              onChange={(e) =>
                setData(
                  "timezone_id",
                  e.target.value ? Number(e.target.value) : "",
                )
              }
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            >
              <option value="">-- Select --</option>
              {time_zones.map((tz) => (
                <option key={tz.id} value={tz.id}>
                  {tz.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Taken By</label>
            <select
              value={data.user_id}
              onChange={(e) =>
                setData("user_id", e.target.value ? Number(e.target.value) : "")
              }
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            >
              <option value="">-- Select --</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
          </div>

          {reports.length > 0 && (
            <div>
              <label className="mb-1 block text-sm font-medium">Report</label>
              <select
                value={data.case_report_id}
                onChange={(e) =>
                  setData(
                    "case_report_id",
                    e.target.value ? Number(e.target.value) : "",
                  )
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              >
                <option value="">-- None --</option>
                {reports.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium">Notes</label>
            <textarea
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              rows={4}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            />
          </div>

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={data.significant === 1}
                onChange={(e) =>
                  setData("significant", e.target.checked ? 1 : 0)
                }
                className="rounded"
              />
              <span className="text-sm">Use on Final Report</span>
            </label>
          </div>

          <div className="flex items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-6 py-2 text-white disabled:opacity-50"
            >
              {processing ? "Saving..." : "Save"}
            </button>
            <Link
              href={`${basePath}/${photo.id}`}
              className="text-hpi-active-blue text-sm hover:underline"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}

function buildDateTime(date: string | null, time: string | null): string {
  if (!date) return ""
  const t = time ?? "00:00:00"
  return `${date}T${t}`
}
